import { useState, MouseEvent, useRef, useEffect } from "react"
import { Menu, MenuItem } from "@mui/material"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import { useTranslation } from "react-i18next"

import "./sectionDivider.scss"

interface SectionDividerProps {
  position: number
  onAddSection: (sectionType: string, position: number) => void
  availableSections: string[]
  isVisible: boolean
  onMenuStateChange?: (isOpen: boolean) => void
  sectionCounts: Record<string, number>
}

export default function SectionDivider({
  position,
  onAddSection,
  availableSections,
  isVisible,
  onMenuStateChange,
  sectionCounts,
}: SectionDividerProps) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isButtonHovered, setIsButtonHovered] = useState(false)
  const dividerRef = useRef<HTMLDivElement>(null)
  const open = Boolean(anchorEl)

  useEffect(() => {
    onMenuStateChange?.(open)
  }, [open, onMenuStateChange])

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setIsButtonHovered(false)
  }

  const handleSelectSection = (sectionType: string) => {
    onAddSection(sectionType, position)
    handleClose()
  }

  return (
    <>
      <div
        className={`SectionDivider ${open ? "menu-open" : ""} ${!isVisible ? "hidden" : ""}`}
        ref={dividerRef}>
        <button
          className={`add-section-button ${open ? "menu-open" : ""}`}
          onClick={handleClick}
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}>
          <Icon type="add" size={20} color={colors.neutral.white} />
        </button>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            className: "section-divider-menu",
          },
        }}>
        {availableSections.map((sectionType) => {
          const count = sectionCounts[sectionType] || 0
          const isDisabled = count >= 3

          return (
            <MenuItem
              key={sectionType}
              onClick={() => !isDisabled && handleSelectSection(sectionType)}
              disabled={isDisabled}
              className={isDisabled ? "disabled" : ""}>
              <Icon
                type="add"
                size={20}
                color={isDisabled ? colors.neutral.grey[400] : colors.neutral.grey[500]}
              />
              <span className="menu-item-text">{t(sectionType)}</span>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
